import { useState } from 'react'
import { CaretRight, Trash } from '@phosphor-icons/react'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n, type MessageKey } from '@/i18n'
import { newId } from '@/lib/ids'
import { enqueueCommands, type EnqueueInput } from '@/offline'
import {
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChoiceGroup,
  EmptyState,
  Field,
  IconButton,
  PickerSheet,
  ScanField,
  useToast,
  type ScanMeta,
} from '@/ui'
import {
  CORRECTION_KINDS,
  RUN_CAUSES,
  type CorrectionKind,
  type CorrectRunPayload,
  type RunCause,
} from '../commands'
import '../commands'
import { liveOutputs, runParentIds, type ProcessState, type RunView } from '../projection'
import { computeBalance, pctToBp } from '../model/balance'
import { checkFreshCode, classifyCode, needsSecondEntry, runFigures, unknownInputWeights } from '../model/rules'
import { Line, ReadingField, type Reading } from '../components/bits'
import { useKgPct, useRefusal } from '../components/useText'
import { outputName, stepName } from '../components/labels'
import { BalancePanel } from '../components/RunCard'
import { ProcessScreen } from '../components/ProcessScreen'
import { useDraftState } from '../useProcess'

/** F6-B1.b — Investigate a held run on the supervisor's own phone and login. */
export function InvestigatePage() {
  const [runId, setRunId] = useState<string | null>(null)
  return (
    <ProcessScreen title="process.investigate.title">
      {(state) => {
        const run = runId ? state.runs.get(runId) : undefined
        return run ? (
          <Investigate state={state} run={run} onBack={() => setRunId(null)} />
        ) : (
          <FlaggedList state={state} onPick={setRunId} />
        )
      }}
    </ProcessScreen>
  )
}

function FlaggedList({ state, onPick }: { state: ProcessState; onPick: (id: string) => void }) {
  const { t, locale } = useI18n()
  const runs = [...state.runs.values()].filter((r) => r.status === 'EXCEPTION')
  if (!runs.length) return <EmptyState title={t('process.investigate.none')} description={t('process.investigate.noneBody')} />
  return (
    <Card>
      <CardHeader eyebrow={t('process.investigate.flagged')} />
      <CardBody className="py-0">
        <ul>
          {runs.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onPick(r.id)}
                className="flex w-full items-center gap-3 border-b border-rule/70 py-3 text-left last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[15px] font-bold">{r.code}</div>
                  <div className="text-[13px] text-muted">
                    {state.machines.get(r.equipment_id)?.code} · {stepName(state, r.step_code, locale)} ·{' '}
                    {r.farmer_code ?? ''}
                  </div>
                </div>
                <CaretRight aria-hidden="true" className="text-muted" />
              </button>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  )
}

interface Draft {
  cause: RunCause | null
  corrections: CorrectRunPayload[]
}

/** The run's figures as they will be after these corrections (Left over now). */
export function correctedFigures(state: ProcessState, run: RunView, list: CorrectRunPayload[]) {
  const outputs = run.outputs.map((o) => ({ ...o }))
  const inputs = [...run.inputs]
  for (const c of list) {
    if (c.kind === 'REWEIGH_OUTPUT' || c.kind === 'VOID_OUTPUT') {
      const o = outputs.find((x) => x.container_id === c.container_id)
      if (!o) continue
      if (c.kind === 'VOID_OUTPUT') o.voided = true
      else o.net_g = (c.weight_read_g ?? o.read_g) - o.tare_g
    } else if (c.kind === 'ADD_OUTPUT') {
      const tare = state.pack.sack_types.find((s) => s.id === c.sack_type_id)?.tare_g ?? 0
      outputs.push({
        container_id: c.new_container_id!,
        unit_id: c.new_unit_id ?? '',
        stream_key: c.stream_key!,
        qr_payload: c.qr_payload,
        sack_type_id: c.sack_type_id,
        tare_g: tare,
        read_g: c.weight_read_g!,
        net_g: c.weight_read_g! - tare,
        stream: 'CONV',
        grade: null,
        voided: false,
        local: true,
      })
    } else if (c.kind === 'ADD_INPUT') {
      const b = c.container_id ? state.bags.get(c.container_id) : undefined
      inputs.push({
        id: c.correction_id,
        container_id: c.container_id,
        unit_id: b?.unit_id ?? null,
        qr_payload: c.qr_payload,
        // Unknown stays null (never 0): see unknown_inputs.
        weight_g:
          c.weight_read_g !== null
            ? c.weight_read_g - (b?.tare_g ?? 0)
            : (b?.issue_net_g ?? b?.net_g_current ?? null),
        context: 'ISSUE',
        local: true,
      })
    }
  }
  return {
    ...runFigures({ ...run, outputs, inputs }, state.steps.get(run.step_code)),
    /** Bags in whose weight is not known here: the figures leave them out. */
    unknown_inputs: unknownInputWeights({ inputs }),
  }
}

function Investigate({ state, run, onBack }: { state: ProcessState; run: RunView; onBack: () => void }) {
  const { t, locale } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const actor = useActor()
  const refusal = useRefusal()
  const kgPct = useKgPct()
  const [d, update, loaded, clear] = useDraftState<Draft>(
    `process.investigate:${run.id}`,
    'process.investigate',
    () => ({ cause: null, corrections: [] }),
    actor.user_id,
  )
  const [kind, setKind] = useState<CorrectionKind | null>(null)
  const [tag, setTag] = useState<{ code: string; entry: ScanMeta['entry'] } | null>(null)
  const [outKey, setOutKey] = useState<string | null>(null)
  const defaultSack = state.pack.sack_types.find((s) => s.default_output) ?? state.pack.sack_types[0]
  const [sackId, setSackId] = useState<string | null>(defaultSack?.id ?? null)
  const [reading, setReading] = useState<Reading | null>(null)
  const [formKey, setFormKey] = useState(0)
  const [saving, setSaving] = useState(false)

  if (!loaded) return null
  const own = run.operator_id === actor.user_id || run.cleanout_by_id === actor.user_id
  if (own)
    return (
      <div className="flex flex-col gap-4">
        <Banner tone="error" title={t('process.investigate.ownTitle')}>
          {t('process.investigate.ownBody')}
        </Banner>
        <Button variant="secondary" size="touch" onClick={onBack}>
          {t('common.back')}
        </Button>
      </div>
    )

  const step = state.steps.get(run.step_code)
  const exception = run.open_exceptions.find((e) => e.type === 'BALANCE_OUT_OF_BAND') ?? run.open_exceptions[0]
  const band = state.pack.yield_bands.find((b) => b.id === run.yield_band_id)
  const fig = correctedFigures(state, run, d.corrections)
  const now = computeBalance(fig, {
    loss_limit_bp: pctToBp(step?.loss_limit_pct) ?? 50,
    min_bp: pctToBp(band?.min_pct),
    max_bp: pctToBp(band?.max_pct),
  })
  const outputsLive = liveOutputs(run)
  const needsWeight = kind === 'REWEIGH_OUTPUT' || kind === 'ADD_OUTPUT'

  const checkTag = (c: string): { error: string | null; container_id: string | null } => {
    if (!kind) return { error: null, container_id: null }
    if (kind === 'ADD_OUTPUT') {
      const v = checkFreshCode(state, c, 'OUTPUT_BAG', actor.user_id)
      return { error: v.ok ? null : refusal(v.reason), container_id: null }
    }
    if (kind === 'ADD_INPUT') {
      const k = classifyCode(state, c)
      if (k.kind !== 'BAG') return { error: refusal('TAG_WRONG_KIND'), container_id: null }
      if (!run.issued.has(k.bag.container_id)) return { error: refusal('NOT_ISSUED'), container_id: null }
      if (run.inputs.some((i) => i.container_id === k.bag.container_id))
        return { error: refusal('DUPLICATE'), container_id: null }
      return { error: null, container_id: k.bag.container_id }
    }
    const o = outputsLive.find((x) => x.qr_payload.toUpperCase() === c.trim().toUpperCase())
    return o ? { error: null, container_id: o.container_id } : { error: refusal('NOT_THIS_BATCH'), container_id: null }
  }

  const tagOk = tag ? checkTag(tag.code) : null
  const canRecord =
    !!kind &&
    !!d.cause &&
    !!tag &&
    tagOk?.error === null &&
    (!needsWeight || !!reading) &&
    (kind !== 'ADD_OUTPUT' || (!!outKey && !!sackId))

  const record = () => {
    if (!canRecord || !kind || !tag || !d.cause) return
    const sameUnit = outKey ? outputsLive.find((o) => o.stream_key === outKey)?.unit_id : undefined
    const c: CorrectRunPayload = {
      correction_id: newId(),
      run_id: run.id,
      exception_id: exception?.id ?? null,
      kind,
      qr_payload: tag.code,
      entry: tag.entry,
      container_id: tagOk?.container_id ?? null,
      new_container_id: kind === 'ADD_OUTPUT' ? newId() : null,
      new_unit_id: kind === 'ADD_OUTPUT' ? (sameUnit ?? newId()) : null,
      stream_key: kind === 'ADD_OUTPUT' ? outKey : null,
      sack_type_id: kind === 'ADD_OUTPUT' ? sackId : null,
      weight_read_g: needsWeight ? reading!.read_g : null,
      reason_code: d.cause,
    }
    update((x) => ({ ...x, corrections: [...x.corrections, c] }))
    setTag(null)
    setReading(null)
    setFormKey((n) => n + 1)
  }

  const recheck = async () => {
    if (!d.cause || d.corrections.length === 0) return
    setSaving(true)
    try {
      const inputs: EnqueueInput[] = d.corrections.map((c, i) => ({
        id: c.correction_id,
        type: 'correct_run',
        payload: { ...c, reason_code: d.cause! },
        actorId: actor.user_id,
        ...(i === 0 ? { parentIds: runParentIds(run) } : {}),
      }))
      await enqueueCommands(inputs)
      await clear()
      toast.success(t('process.investigate.sent'))
      onBack()
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardBody className="py-1">
          <Line label={t('process.investigate.run')} value={run.code} />
          <Line
            label={t('process.investigate.machine')}
            value={`${state.machines.get(run.equipment_id)?.code ?? ''} · ${stepName(state, run.step_code, locale)}`}
          />
        </CardBody>
      </Card>
      <BalancePanel state={state} run={run} />

      <Field label={<BilingualLabel k="process.investigate.cause" />} required>
        <PickerSheet
          title={<BilingualLabel k="process.investigate.cause" />}
          options={RUN_CAUSES.map((c) => ({ value: c, label: t(`process.cause.${c}` as MessageKey) }))}
          value={d.cause}
          onChange={(v) => update((x) => ({ ...x, cause: v }))}
        />
      </Field>

      <Card>
        <CardHeader eyebrow={t('process.investigate.correction')} />
        <CardBody key={formKey} className="flex flex-col gap-3">
          <ChoiceGroup
            layout="stack"
            options={CORRECTION_KINDS.map((k) => ({ value: k, label: t(`process.correction.${k}` as MessageKey) }))}
            value={kind}
            onChange={(k) => {
              setKind(k)
              setTag(null)
              setReading(null)
            }}
          />
          {kind === 'ADD_OUTPUT' && (
            <>
              <Field label={<BilingualLabel k="process.weighOut.output" />} required>
                <ChoiceGroup
                  options={(step?.outputs ?? []).map((o) => ({ value: o.key, label: locale === 'hi' ? o.hi : o.en }))}
                  value={outKey}
                  onChange={setOutKey}
                />
              </Field>
              <Field label={<BilingualLabel k="process.weighOut.sackType" />} required>
                <ChoiceGroup
                  options={state.pack.sack_types.map((s) => ({ value: s.id, label: `${s.name} · ${fmt.weight(s.tare_g)}` }))}
                  value={sackId}
                  onChange={setSackId}
                  layout="stack"
                />
              </Field>
            </>
          )}
          {kind && (
            <Field label={<BilingualLabel k="process.investigate.tag" />} required error={tagOk?.error ?? undefined}>
              <ScanField
                value={tag?.code ?? null}
                validate={(c) => checkTag(c).error}
                onChange={(c, meta) => setTag(c ? { code: c.trim().toUpperCase(), entry: meta?.entry ?? 'SCAN' } : null)}
              />
            </Field>
          )}
          {needsWeight && (
            <ReadingField
              label={<BilingualLabel k="process.investigate.weight" />}
              needsTwice={(g) => needsSecondEntry(g, state.pack.settings)}
              onChange={setReading}
            />
          )}
          <Button size="touch" variant="secondary" disabled={!canRecord} onClick={record}>
            <BilingualLabel k="process.investigate.record" />
          </Button>
        </CardBody>
      </Card>

      {d.corrections.length > 0 && (
        <Card>
          <CardHeader eyebrow={t('process.investigate.recorded', { n: d.corrections.length })} />
          <CardBody className="py-1">
            <ul>
              {d.corrections.map((c) => (
                <li key={c.correction_id} className="flex items-center gap-2 border-b border-rule/70 py-2 last:border-b-0">
                  <div className="min-w-0 flex-1 text-[14px]">
                    <div>{t(`process.correction.${c.kind}` as MessageKey)}</div>
                    <div className="font-mono text-[12px] text-muted">
                      {c.qr_payload}
                      {c.stream_key ? ` · ${outputName(state, run.step_code, c.stream_key, locale, t)}` : ''}
                      {c.weight_read_g ? ` · ${fmt.weight(c.weight_read_g)}` : ''}
                    </div>
                  </div>
                  <IconButton
                    size="touch"
                    label={t('process.remove')}
                    icon={<Trash />}
                    onClick={() =>
                      update((x) => ({ ...x, corrections: x.corrections.filter((y) => y.correction_id !== c.correction_id) }))
                    }
                  />
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody className="py-1">
          <Line
            label={<BilingualLabel k="process.investigate.leftNow" />}
            value={kgPct(now.residual_g, now.residual_bp)}
            strong
            tone={now.pass ? 'ok' : 'warn'}
          />
        </CardBody>
      </Card>
      {d.corrections.length === 0 && <p className="text-[13px] text-muted">{t('process.investigate.noCorrectionHint')}</p>}
      <Button size="xl" fullWidth disabled={!d.cause || d.corrections.length === 0} loading={saving} onClick={() => void recheck()}>
        <BilingualLabel k="process.investigate.recheck" />
      </Button>
      <Button variant="ghost" size="touch" onClick={onBack}>
        {t('common.back')}
      </Button>
    </div>
  )
}
